import { AlertCircle, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui';

export interface EmptyStateProps {
  icon?: LucideIcon;
  title?: string;
  message?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
  compact?: boolean;
}

/**
 * Reusable empty state component for lists/grids with zero items.
 * Includes optional CTA button to guide user to next action.
 * Supports both `message` and `description` for flexibility.
 */
export function EmptyState({
  icon: Icon = AlertCircle,
  title,
  message,
  description,
  action,
  className = '',
  compact = false,
}: EmptyStateProps) {
  const displayText = message ?? description ?? '';

  return (
    <div
      className={[
        'flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/60 text-gray-400',
        compact ? 'py-12 px-6' : 'py-20 px-8',
        className,
      ].join(' ')}
    >
      <Icon className={compact ? 'w-8 h-8 mb-2' : 'w-10 h-10 mb-3'} />
      {title && <p className="text-sm font-medium text-gray-700">{title}</p>}
      {displayText && (
        <p className={`mt-1 max-w-sm text-center ${compact ? 'text-xs' : 'text-sm'}`}>
          {displayText}
        </p>
      )}
      {action && (
        <Button
          variant="primary"
          onClick={action.onClick}
          className="mt-4 w-fit px-4 py-2 text-white rounded-md text-sm font-medium hover: transition-colors"
        >
          {action.label}
        </Button>
      )}
    </div>
  );
}
